import { useEffect, useRef, useCallback } from 'react'
import { useLocation } from 'react-router-dom'

const STORAGE_KEY = 'interstitial_ad_state'
const MAX_INTERSTITIALS_PER_SESSION = 3
const NAVIGATION_THRESHOLD = 3 // Show ad every 3rd navigation

interface InterstitialState {
  navigationCount: number
  interstitialsShown: number
  sessionStart: number
}

function getStoredState(): InterstitialState {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY)
    if (stored) {
      const state = JSON.parse(stored) as InterstitialState
      // Reset if session is older than 30 minutes
      if (Date.now() - state.sessionStart > 30 * 60 * 1000) {
        return createInitialState()
      }
      return state
    }
  } catch {
    // Ignore parsing errors
  }
  return createInitialState()
}

function createInitialState(): InterstitialState {
  return {
    navigationCount: 0,
    interstitialsShown: 0,
    sessionStart: Date.now(),
  }
}

function saveState(state: InterstitialState) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Ignore storage errors
  }
}

export function useInterstitialAd(onShowAd: () => void) {
  const location = useLocation()
  const stateRef = useRef<InterstitialState>(getStoredState())
  const isInitialMount = useRef(true)
  const previousPath = useRef(location.pathname)

  const checkAndShowAd = useCallback(() => {
    const state = stateRef.current

    // Don't show if we've hit the max for this session
    if (state.interstitialsShown >= MAX_INTERSTITIALS_PER_SESSION) {
      return
    }

    // Check if we should show an ad
    if (state.navigationCount > 0 && state.navigationCount % NAVIGATION_THRESHOLD === 0) {
      state.interstitialsShown++
      saveState(state)
      onShowAd()
    }
  }, [onShowAd])

  useEffect(() => {
    // Skip initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false
      previousPath.current = location.pathname
      return
    }

    // Only count actual page changes (not hash or search changes)
    if (location.pathname !== previousPath.current) {
      previousPath.current = location.pathname
      stateRef.current.navigationCount++
      saveState(stateRef.current)
      checkAndShowAd()
    }
  }, [location, checkAndShowAd])

  // Reset state function (useful for testing)
  const resetState = useCallback(() => {
    stateRef.current = createInitialState()
    saveState(stateRef.current)
  }, [])

  return {
    navigationCount: stateRef.current.navigationCount,
    interstitialsShown: stateRef.current.interstitialsShown,
    resetState,
  }
}
